import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const EVENT_TYPE_COLORS = {
  visit: '#3b82f6', errand: '#f59e0b', google: '#10b981',
  birthday: '#ec4899', invoice: '#ef4444', expiration: '#f97316',
  vacation: '#8b5cf6', absence: '#6b7280',
};

export const meta = {
  id: 'today_events',
  category: 'calendar',
  titleKey: 'dashboard.widgets.todayEvents',
  descriptionKey: 'dashboard.widgets.todayEventsDesc',
  defaultSize: 'sm',
  requiresModule: 'calendar',
  icon: CalendarDays,
};

// Compact today-only events. Distinct from UpcomingEvents (7d) so an agent
// can put both side-by-side: "what's happening today" + "what's coming up."
// Shares the React Query cache for same-day window with UpcomingEvents.
export default function TodayEventsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = user?.language === 'en' ? enUS : es;

  const from = format(startOfDay(new Date()), 'yyyy-MM-dd');
  const to = format(endOfDay(new Date()), 'yyyy-MM-dd');
  const { data: events = [] } = useQuery({
    queryKey: ['dashboard-events-today', from],
    queryFn: () => api.get(`/calendar/events?from=${from}&to=${to}`).then(r => r.data),
    refetchInterval: 5 * 60 * 1000,
  });

  const todayEvents = [...events].sort((a, b) => new Date(a.start) - new Date(b.start));

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarDays size={16} />
          {t('dashboard.widgets.todayEvents')}
        </div>
        <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
          {format(new Date(), 'EEE d MMM', { locale })}
        </span>
      </div>
      {todayEvents.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>
          {t('dashboard.widgets.todayEventsEmpty', 'Nothing scheduled today')}
        </div>
      ) : (
        todayEvents.slice(0, 6).map((ev, i) => {
          const evDate = new Date(ev.start);
          const color = EVENT_TYPE_COLORS[ev.type] || '#6b7280';
          return (
            <div
              key={ev.id || i}
              onClick={() => navigate('/calendar')}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px',
                borderBottom: '1px solid var(--c-border-subtle)',
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ width: 3, height: 24, borderRadius: 2, background: color, flexShrink: 0 }} />
              <div style={{ width: 44, fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', flexShrink: 0 }}>
                {ev.allDay ? t('dashboard.allDay') : format(evDate, 'HH:mm')}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ev.title}
                </div>
                {ev.company && (
                  <div style={{ fontSize: 10, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ev.company}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
